import { ContentKind, MediaFormat } from 'models/socialCampaign'
import { EditorMedia, MAX_POST_MEDIA, editorMediaOfItem, sameVisual } from 'utils/socialCampaign/postEditor'
import { FC, KeyboardEvent, useId, useMemo, useState } from 'react'
import { GRAY_700, GRAY_800, SELECTED_BLUE } from 'constants/styling/theme'
import { SocialCampaignTarget, useGetSocialCampaignContentInventory } from 'dataQueries/socialCampaign.query'

import { CONTENT_KIND_WAYS } from './contentKinds'
import CircularProgress from '@mui/material/CircularProgress'
import { FilterTab } from 'components/common/FilterTab'
import { MUIButton } from 'components/common/MUIButton'
import { MediaThumb } from 'components/common/MediaThumb'
import Modal from 'components/common/Modals/Modal/Modal'
import { ProductIcon } from 'components/common/ProductIcon'
import { SmartDisplayOutlined } from '@mui/icons-material'
import Stack from '@mui/material/Stack'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import styles from './PostVisualsPicker.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  target: SocialCampaignTarget
  /** The post's visuals when the picker opens: they start picked, in their order */
  picked: EditorMedia[]
  /** Over the editor: paints above it */
  zIndex?: number
  onUse: (media: EditorMedia[]) => void
  onCancel: () => void
}

/**
 * "Add visuals" (prototype v3 `renderPicker`), mounted while open: the whole gallery of the property for one post, by
 * kind of content, the kinds left out of the campaign included (a post may still use them). The order the visuals are
 * picked in is the order they go out; a picked visual shows its number. The viewer's content inventory feeds it, so a
 * visual the viewer's seat does not show is not offered, and one already in the post stays where it is.
 */
export const PostVisualsPicker: FC<Props> = ({ target, picked, zIndex, onUse, onCancel }) => {
  const { t } = useTranslation(['social_campaign'])
  const id = `campaign-editor-picker-${useId().replace(/:/g, '')}`
  const inventory = useGetSocialCampaignContentInventory(target)
  // mounted for each opening: it starts from the post's visuals
  const [selection, setSelection] = useState<EditorMedia[]>(picked)
  const [tab, setTab] = useState<ContentKind>(ContentKind.PHOTOS)

  const kinds = useMemo(() => (inventory.data?.kinds ?? []).filter((kind) => kind.items.length > 0), [inventory.data])
  const shownKind = kinds.find((kind) => kind.kind === tab) ?? kinds[0]
  const full = selection.length >= MAX_POST_MEDIA

  const toggle = (media: EditorMedia) => {
    setSelection((current) => current.some((item) => sameVisual(item.key, media.key))
      ? current.filter((item) => !sameVisual(item.key, media.key))
      : current.length >= MAX_POST_MEDIA ? current : [...current, media])
  }

  const onTabsKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const at = kinds.findIndex((kind) => kind.kind === shownKind?.kind)
    const next = kinds[(at + (event.key === 'ArrowRight' ? 1 : -1) + kinds.length) % kinds.length]
    if (!next) return
    setTab(next.kind)
    document.getElementById(`${id}-tab-${next.kind}`)?.focus()
  }

  return (
    <Modal
      isOpen
      isAccessibleDialog
      size="md"
      isSheetOnPhone
      hasHeaderContentDivider
      zIndex={zIndex}
      title={t('editor.picker.title')}
      subtitle={t('editor.picker.description')}
      closeLabel={t('editor.close')}
      onClose={onCancel}
      onClickOutside={onCancel}
      footerStart={<Typography variant="text-sm" component="span" color={GRAY_700}>{t('editor.picker.selected', { count: selection.length })}</Typography>}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap={1}>

          <MUIButton type="secondaryBorder" onClick={onCancel}>{t('editor.picker.cancel')}</MUIButton>

          <MUIButton disabled={selection.length === 0} onClick={() => onUse(selection)}>{t('editor.picker.use', { count: selection.length })}</MUIButton>

        </Stack>
      }
    >
      <div className={styles.body}>

        {inventory.isPending &&
          <div className={styles.loading}><CircularProgress size="2.4rem" aria-label={t('editor.picker.loading')} /></div>
        }

        {inventory.isError &&
          <SystemMessage
            variant="error"
            role="alert"
            message={t('editor.picker.error')}
            isRawContent
            actions={<MUIButton type="secondaryBorder" size="sm" onClick={() => void inventory.refetch()}>{t('editor.picker.retry')}</MUIButton>}
          />
        }

        {inventory.isSuccess && !shownKind && <Typography variant="text-sm" component="p" color={GRAY_800}>{t('editor.picker.empty')}</Typography>}

        {inventory.isSuccess && shownKind &&
          <div className={styles.tabs} role="tablist" aria-label={t('editor.picker.gallery')} onKeyDown={onTabsKey}>
            {kinds.map((kind) => {
              const selected = kind.kind === shownKind.kind
              return (
                <FilterTab
                  key={kind.kind}
                  role="tab"
                  id={`${id}-tab-${kind.kind}`}
                  ariaSelected={selected}
                  ariaControls={`${id}-grid`}
                  tabIndex={selected ? 0 : -1}
                  isActive={selected}
                  height="4rem"
                  sx={{ flex: 'none' }}
                  icon={<ProductIcon kind={CONTENT_KIND_WAYS[kind.kind].icon} size="md" background={selected ? 'white' : 'beige'} />}
                  label={t(`plan.kind.${kind.kind}`)}
                  totalCount={String(kind.items.length)}
                  onClick={() => setTab(kind.kind)}
                />
              )
            })}
          </div>
        }

        {inventory.isSuccess && shownKind && full && <Typography variant="text-sm" component="p" color={GRAY_800}>{t('editor.picker.max', { count: MAX_POST_MEDIA })}</Typography>}

        {inventory.isSuccess && shownKind &&
          <ul className={styles.grid} role="tabpanel" id={`${id}-grid`} aria-labelledby={`${id}-tab-${shownKind.kind}`}>
            {shownKind.items.map((item, index) => {
              const media = editorMediaOfItem(item)
              const at = selection.findIndex((chosen) => sameVisual(chosen.key, media.key))
              const name = item.label ?? item.roomLabel
              const video = item.format === MediaFormat.VIDEO
              const url = video ? item.previewUrl : item.thumbnailUrl ?? item.previewUrl
              return (
                <li key={`${item.assignmentPublicId}/${item.visualType}/${item.filename}`}>
                  <button
                    type="button"
                    className={styles.tile}
                    aria-pressed={at !== -1}
                    aria-label={name ?? t('editor.picker.tile', { kind: t(`plan.kind.${item.contentKind}`), index: index + 1 })}
                    aria-disabled={at === -1 && full ? 'true' : undefined}
                    onClick={() => toggle(media)}
                  >

                    {/* inline: the picked colour must win over the frame's transparent border */}
                    <span className={styles.frame} style={at !== -1 ? { borderColor: SELECTED_BLUE } : undefined}>
                      {video
                        ? <MediaThumb kind="video" src={url ? `${url}#t=0.1` : undefined} alt="" kindIcon={<SmartDisplayOutlined />} />
                        : <MediaThumb src={url ?? undefined} alt="" />
                      }
                    </span>

                    {at !== -1 && <span className={styles.number} aria-hidden="true">{at + 1}</span>}

                    {name && <Typography variant="text-sm" component="span" color={GRAY_800} aria-hidden="true">{name}</Typography>}

                  </button>
                </li>
              )
            })}
          </ul>
        }

      </div>
    </Modal>
  )
}
